import React from 'react'
import { useTranslation } from 'react-i18next'
import { NoData, NoResults, RowsSkeletonLoader } from '@node-space/ui-components'
import { RequestErrorStatusCard } from 'components/Errors/RequestErrorStatusCard'
import { MIN_PAGINATION_SIZE } from 'constants/queries'
import { useCardTransactionsQuery } from 'hooks/queries/Cards/useCardTransactionsQuery'
import { useSearchablePagination } from 'hooks/useSearchablePagination'
import { CardTransactionsDataTable } from './CardTransactionsDataTable'

interface CardTransactionsContentProps {
  queryString: string | undefined
  isSearching: boolean
  onClearSearch: () => void
}

export const CardTransactionsContent = ({
  queryString,
  isSearching,
  onClearSearch,
}: CardTransactionsContentProps) => {
  const { t } = useTranslation()
  const { page, rowsPerPage, handleChangePage, handleChangeRowsPerPage } =
    useSearchablePagination(MIN_PAGINATION_SIZE)

  const { data, isFetching, isError, refetch } = useCardTransactionsQuery({
    pageNumber: page,
    pageSize: rowsPerPage,
    q: queryString,
  })

  const transactions = data?.content ?? []
  const hasTransactions = transactions.length > 0

  if (isError) {
    return <RequestErrorStatusCard onReload={refetch} />
  }

  if (isFetching) {
    return <RowsSkeletonLoader rows={MIN_PAGINATION_SIZE} />
  }

  if (!hasTransactions && !isSearching) {
    return (
      <NoData
        dataName="card transactions"
        description={t('customers.cardTransactionsTable.emptyStateDescription')}
      />
    )
  }

  if (!hasTransactions) {
    return <NoResults onClear={onClearSearch} />
  }

  return (
    <CardTransactionsDataTable
      transactions={transactions}
      page={page}
      rowsPerPage={rowsPerPage}
      totalElements={data?.totalElements ?? 0}
      onChangePage={handleChangePage}
      onChangeRowsPerPage={handleChangeRowsPerPage}
    />
  )
}
